"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function HigherOrderAssets() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="233529" id="vítejte-ve-světě-smyslů" className="bg-background text-foreground relative min-h-[100svh] flex flex-col lg:flex-row overflow-hidden">
      {}
      <div className="w-full lg:w-[40%] flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-24 lg:py-0 z-10 relative">
        <div ref={textRef} className={`transition-all duration-1000 ease-out flex flex-col items-start ${isTextVisible ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"}`}>
          <p className="text-primary font-default tracking-widest uppercase text-2xl text-center w-full ![margin:-200px_0px_100px_0px] ![padding:20px_0px_8px_0px]">
            Brzy otevíráme
          </p>
          <p className="text-primary font-default tracking-widest uppercase text-sm mb-6">
            SENSU • Umění intimity
          </p>
          
          <h1 className="font-serif text-primary text-7xl sm:text-8xl lg:text-[8rem] leading-[0.85] tracking-tighter uppercase mb-4">
            SENSU
          </h1>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-foreground mb-8">
            Vítejte ve světě <br />
            <span className="text-muted-foreground text-2xl sm:text-3xl">vašich smyslů</span>
          </h2>
          
          <p className="text-muted-foreground text-lg max-w-md font-default mb-12 leading-relaxed">
            Objevte prostor, kde se čas zastaví. Exkluzivní erotické masáže v luxusním prostředí, které probudí vaši životní energii.
          </p>

          <Link to="/ContactPage">
            <Button size="lg" className="rounded-full font-default tracking-widest uppercase px-8 py-6 text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-all hover:scale-105 animate-pulse border border-primary/50">
              Rezervovat rituál
            </Button>
          </Link>
        </div>
      </div>

      {}
      <div className="w-full lg:w-[60%] relative min-h-[60vh] lg:min-h-screen order-first lg:order-last">
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent lg:hidden z-10"></div>
        <div className="absolute inset-0 bg-black/40 z-[5]"></div>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/sensu_silk_silhouette.png" alt="Ethereal silhouette on silk sheets" className="absolute inset-0 w-full h-full object-cover object-center" loading="lazy" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/sensu_silk_silhouette.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/sensu_silk_silhouette.png-wvc-sizes" />
      </div>

      {}
      <div className="absolute z-20 top-[60vh] lg:top-1/2 left-1/2 lg:left-[40%] -translate-x-1/2 -translate-y-1/2 w-max">
        <Badge variant="secondary" className="inline-flex items-center justify-center font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-secondary/90 px-6 py-4 text-base font-serif tracking-widest uppercase shadow-3xl rounded-full border border-primary/30 bg-background/80 backdrop-blur-xl text-primary ![margin:-50px_0px_0px_0px] dark:aria-invalid:ring-destructive/40 sm:text-lg lg:text-xl">
          Luxusní zážitek
        </Badge>
      </div>
    </section>;
}